import React, { useState, useEffect } from 'react';
import { Loader2, Eye, EyeOff, CheckCircle2, XCircle } from 'lucide-react';
import axios from '../../utils/axios';

const fieldCls =
  'w-full px-4 py-3 rounded-xl bg-white border border-gray-200 text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500/40 focus:border-green-500 transition-all';

type Phase = 'form' | 'success' | 'invalid';

export function ResetPassword() {
  const [token,       setToken]       = useState('');
  const [password,    setPassword]    = useState('');
  const [confirm,     setConfirm]     = useState('');
  const [showPass,    setShowPass]    = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading,     setLoading]     = useState(false);
  const [phase,       setPhase]       = useState<Phase>('form');
  const [errorMsg,    setErrorMsg]    = useState('');

  /* Read token from URL on mount */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('token');
    if (!t) {
      setPhase('invalid');
    } else {
      setToken(t);
    }
  }, []);

  const passwordsMatch = confirm === '' || password === confirm;
  const strong = password.length >= 8;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!strong)            return setErrorMsg('Password must be at least 8 characters.');
    if (password !== confirm) return setErrorMsg('Passwords do not match.');

    setLoading(true);
    setErrorMsg('');
    axios
      .post('/users/reset-password', { token, newPassword: password })
      .then(() => setPhase('success'))
      .catch((err: any) => {
        const msg = err?.response?.data?.message || 'This reset link is invalid or has expired.';
        setErrorMsg(msg);
        if (msg.toLowerCase().includes('expired') || msg.toLowerCase().includes('invalid')) {
          setPhase('invalid');
        }
      })
      .finally(() => setLoading(false));
  };

  /* ── Invalid / expired token ─────────────────────────────────────────── */
  if (phase === 'invalid') {
    return (
      <PageShell>
        <div className="flex flex-col items-center text-center gap-5 py-10">
          <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center">
            <XCircle className="w-7 h-7 text-red-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'var(--font-display)' }}>
            Link expired or invalid
          </h2>
          <p className="text-gray-500 text-sm leading-relaxed max-w-xs" style={{ fontFamily: 'var(--font-body)' }}>
            This password reset link has either expired (links are valid for 1 hour) or has already been used.
          </p>
          <a
            href="/accounts/forgot-password"
            className="mt-2 px-6 py-2.5 rounded-full bg-green-700 text-white text-sm font-semibold hover:bg-green-600 transition-all"
            style={{ fontFamily: 'var(--font-body)' }}
          >
            Request a new link
          </a>
          <a
            href="/accounts/login"
            className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
            style={{ fontFamily: 'var(--font-body)' }}
          >
            Back to sign in
          </a>
        </div>
      </PageShell>
    );
  }

  /* ── Success ──────────────────────────────────────────────────────────── */
  if (phase === 'success') {
    return (
      <PageShell>
        <div className="flex flex-col items-center text-center gap-5 py-10">
          <div className="w-14 h-14 rounded-full bg-green-600 flex items-center justify-center">
            <CheckCircle2 className="w-7 h-7 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'var(--font-display)' }}>
            Password updated!
          </h2>
          <p className="text-gray-500 text-sm leading-relaxed max-w-xs" style={{ fontFamily: 'var(--font-body)' }}>
            Your password has been changed successfully. You can now sign in with your new password.
          </p>
          <a
            href="/accounts/login"
            className="mt-2 px-6 py-2.5 rounded-full bg-green-700 text-white text-sm font-semibold hover:bg-green-600 transition-all"
            style={{ fontFamily: 'var(--font-body)' }}
          >
            Sign In
          </a>
        </div>
      </PageShell>
    );
  }

  /* ── Form ─────────────────────────────────────────────────────────────── */
  return (
    <PageShell>
      <div className="mb-2">
        <a
          href="/accounts/login"
          className="inline-flex items-center gap-1.5 text-gray-400 text-xs hover:text-gray-600 transition-colors mb-8"
          style={{ fontFamily: 'var(--font-body)' }}
        >
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} className="w-3.5 h-3.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18" />
          </svg>
          Back to sign in
        </a>
      </div>

      <h1 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'var(--font-display)' }}>
        Set new password
      </h1>
      <p className="text-gray-500 text-sm mb-8" style={{ fontFamily: 'var(--font-body)' }}>
        Choose a strong password. It must be at least 8 characters.
      </p>

      {errorMsg && (
        <div className="mb-6 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm" style={{ fontFamily: 'var(--font-body)' }}>
          {errorMsg}
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        {/* New password */}
        <div>
          <label htmlFor="newPassword" className="block text-xs font-medium text-gray-600 mb-1.5" style={{ fontFamily: 'var(--font-body)' }}>
            New Password
          </label>
          <div className="relative">
            <input
              id="newPassword"
              type={showPass ? 'text' : 'password'}
              value={password}
              onChange={(e) => { setPassword(e.target.value); setErrorMsg(''); }}
              placeholder="Min. 8 characters"
              required
              className={fieldCls + ' pr-11'}
            />
            <button
              type="button"
              onClick={() => setShowPass((v) => !v)}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label={showPass ? 'Hide password' : 'Show password'}
            >
              {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {/* Strength bar */}
          <div className="mt-2 flex gap-1">
            {[4, 6, 8].map((n) => (
              <div
                key={n}
                className="h-1 flex-1 rounded-full transition-all duration-300"
                style={{
                  background: password.length >= n
                    ? password.length >= 8 ? '#16a34a' : '#f59e0b'
                    : '#e5e7eb',
                }}
              />
            ))}
            <span className="text-[10px] text-gray-400 ml-1" style={{ fontFamily: 'var(--font-body)' }}>
              {password.length === 0 ? '' : password.length < 6 ? 'Weak' : password.length < 8 ? 'Fair' : 'Strong'}
            </span>
          </div>
        </div>

        {/* Confirm password */}
        <div>
          <label htmlFor="confirmPassword" className="block text-xs font-medium text-gray-600 mb-1.5" style={{ fontFamily: 'var(--font-body)' }}>
            Confirm Password
          </label>
          <div className="relative">
            <input
              id="confirmPassword"
              type={showConfirm ? 'text' : 'password'}
              value={confirm}
              onChange={(e) => { setConfirm(e.target.value); setErrorMsg(''); }}
              placeholder="Re-enter your new password"
              required
              className={fieldCls + ' pr-11 ' + (!passwordsMatch ? 'border-red-300 focus:border-red-400 focus:ring-red-400/30' : '')}
            />
            <button
              type="button"
              onClick={() => setShowConfirm((v) => !v)}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label={showConfirm ? 'Hide password' : 'Show password'}
            >
              {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {!passwordsMatch && (
            <p className="mt-1.5 text-xs text-red-500" style={{ fontFamily: 'var(--font-body)' }}>Passwords do not match</p>
          )}
        </div>

        <button
          type="submit"
          disabled={loading || !strong || !passwordsMatch || confirm === ''}
          className="w-full py-3.5 rounded-full bg-green-700 text-white text-sm font-semibold hover:bg-green-600 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-green-200 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0 flex items-center justify-center gap-2"
          style={{ fontFamily: 'var(--font-body)' }}
        >
          {loading && <Loader2 className="animate-spin w-4 h-4" />}
          {loading ? 'Updating password…' : 'Update Password'}
        </button>
      </form>
    </PageShell>
  );
}

/* ── Shared layout shell (mirrors ForgotPassword left/right split) ─────── */
function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-[calc(100vh-120px)] flex" style={{ background: '#fafaf7' }}>

      {/* Left: brand panel */}
      <div
        className="hidden lg:flex lg:w-5/12 xl:w-1/2 flex-col justify-between p-12 xl:p-16 relative overflow-hidden"
        style={{ background: '#052e16' }}
      >
        <img
          src="/images/leopard.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          style={{ opacity: 0.18 }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#052e16]/85 via-[#052e16]/65 to-transparent pointer-events-none" />

        <div className="relative z-10">
          <a href="/" className="flex items-center gap-3">
            <span className="text-2xl font-bold text-white" style={{ fontFamily: 'var(--font-display)' }}>WITS</span>
            <span className="text-green-400 text-xs tracking-[0.15em] uppercase font-medium mt-1" style={{ fontFamily: 'var(--font-body)' }}>
              by LAPA Africa
            </span>
          </a>
        </div>

        <div className="relative z-10">
          <h2
            className="text-4xl xl:text-5xl font-bold text-white leading-[1.1] mb-5"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            Secure access,<br />
            <span className="text-green-400">always protected</span>
          </h2>
          <p className="text-white/55 text-sm leading-relaxed max-w-sm" style={{ fontFamily: 'var(--font-body)' }}>
            Choose a strong, unique password to keep your WITS account secure.
          </p>
          <div
            className="mt-10 p-5 rounded-2xl"
            style={{ background: 'rgba(74,222,128,0.07)', border: '1px solid rgba(74,222,128,0.15)' }}
          >
            <p className="text-green-400 text-xs font-semibold tracking-[0.1em] uppercase mb-2" style={{ fontFamily: 'var(--font-body)' }}>
              Security tip
            </p>
            <p className="text-white/55 text-xs leading-relaxed" style={{ fontFamily: 'var(--font-body)' }}>
              Use a mix of letters, numbers and symbols. Never reuse passwords across different services.
            </p>
          </div>
        </div>
      </div>

      {/* Right: content */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 lg:px-12 xl:px-16">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <a href="/" className="flex items-center gap-2 mb-10 lg:hidden">
            <span className="text-xl font-bold text-gray-900" style={{ fontFamily: 'var(--font-display)' }}>WITS</span>
            <span className="text-green-700 text-xs tracking-[0.12em] uppercase font-medium" style={{ fontFamily: 'var(--font-body)' }}>by LAPA Africa</span>
          </a>
          {children}
        </div>
      </div>
    </div>
  );
}
